import React, { useState } from 'react';
import { Fragment } from 'react';
import { Dialog, Disclosure, Transition } from '@headlessui/react';
import { XMarkIcon, FunnelIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Category } from '../types/woocommerce';
import { ProductQueryParams } from '../types/woocommerce';

interface ProductFiltersProps {
  categories: Category[];
  currentFilters: ProductQueryParams;
  onFiltersChange: (filters: ProductQueryParams) => void;
}

const sortOptions = [
  { name: 'Plus récent', value: { orderby: 'date', order: 'desc' as const } },
  { name: 'Prix croissant', value: { orderby: 'price', order: 'asc' as const } },
  { name: 'Prix décroissant', value: { orderby: 'price', order: 'desc' as const } },
  { name: 'Popularité', value: { orderby: 'popularity', order: 'desc' as const } },
  { name: 'Note moyenne', value: { orderby: 'rating', order: 'desc' as const } },
];

const priceRanges = [
  { name: 'Tous les prix', value: { min_price: undefined, max_price: undefined } },
  { name: 'Moins de 25€', value: { min_price: '0', max_price: '25' } },
  { name: '25€ - 50€', value: { min_price: '25', max_price: '50' } },
  { name: '50€ - 100€', value: { min_price: '50', max_price: '100' } },
  { name: '100€ - 200€', value: { min_price: '100', max_price: '200' } },
  { name: 'Plus de 200€', value: { min_price: '200', max_price: undefined } },
];

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  categories,
  currentFilters,
  onFiltersChange,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const handleFilterChange = (newFilters: Partial<ProductQueryParams>) => {
    onFiltersChange({ ...currentFilters, ...newFilters });
  };

  const handleCategoryChange = (categoryId: string | undefined) => {
    if (categoryId === undefined) {
      const { category, ...rest } = currentFilters;
      onFiltersChange(rest);
    } else {
      handleFilterChange({ category: categoryId });
    }
  };

  const handlePriceRangeChange = (priceRange: { min_price?: string; max_price?: string }) => {
    const { min_price, max_price, ...rest } = currentFilters;
    if (priceRange.min_price || priceRange.max_price) {
      onFiltersChange({ ...rest, ...priceRange });
    } else {
      onFiltersChange(rest);
    }
  };

  const FiltersContent = () => (
    <div className="space-y-6">
      {/* Tri */}
      <Disclosure as="div" defaultOpen className="border-b border-gray-200 pb-6">
        {({ open }) => (
          <>
            <h3 className="-my-3 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                <span className="font-medium text-gray-900">Trier par</span>
                <span className="ml-6 flex items-center">
                  <ChevronDownIcon
                    className={`h-5 w-5 transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </Disclosure.Button>
            </h3>
            <Disclosure.Panel className="pt-6">
              <div className="space-y-2">
                {sortOptions.map((option) => (
                  <label
                    key={option.name}
                    className="flex items-center cursor-pointer hover:text-primary-600"
                  >
                    <input
                      type="radio"
                      name="sort"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      checked={
                        currentFilters.orderby === option.value.orderby &&
                        currentFilters.order === option.value.order
                      }
                      onChange={() => handleFilterChange(option.value)}
                    />
                    <span className="ml-3 text-sm">{option.name}</span>
                  </label>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Catégories */}
      <Disclosure as="div" defaultOpen className="border-b border-gray-200 pb-6">
        {({ open }) => (
          <>
            <h3 className="-my-3 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                <span className="font-medium text-gray-900">Catégories</span>
                <span className="ml-6 flex items-center">
                  <ChevronDownIcon
                    className={`h-5 w-5 transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </Disclosure.Button>
            </h3>
            <Disclosure.Panel className="pt-6">
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer hover:text-primary-600">
                  <input
                    type="radio"
                    name="category"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                    checked={!currentFilters.category}
                    onChange={() => handleCategoryChange(undefined)}
                  />
                  <span className="ml-3 text-sm">Toutes les catégories</span>
                </label>
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className="flex items-center cursor-pointer hover:text-primary-600"
                  >
                    <input
                      type="radio"
                      name="category"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      checked={currentFilters.category === category.id.toString()}
                      onChange={() => handleCategoryChange(category.id.toString())}
                    />
                    <span className="ml-3 text-sm">
                      {category.name} ({category.count})
                    </span>
                  </label>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Prix */}
      <Disclosure as="div" defaultOpen className="border-b border-gray-200 pb-6">
        {({ open }) => (
          <>
            <h3 className="-my-3 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                <span className="font-medium text-gray-900">Prix</span>
                <span className="ml-6 flex items-center">
                  <ChevronDownIcon
                    className={`h-5 w-5 transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </Disclosure.Button>
            </h3>
            <Disclosure.Panel className="pt-6">
              <div className="space-y-2">
                {priceRanges.map((range) => (
                  <label
                    key={range.name}
                    className="flex items-center cursor-pointer hover:text-primary-600"
                  >
                    <input
                      type="radio"
                      name="priceRange"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      checked={
                        currentFilters.min_price === range.value.min_price &&
                        currentFilters.max_price === range.value.max_price
                      }
                      onChange={() => handlePriceRangeChange(range.value)}
                    />
                    <span className="ml-3 text-sm">{range.name}</span>
                  </label>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Disponibilité */}
      <Disclosure as="div" className="border-b border-gray-200 pb-6">
        {({ open }) => (
          <>
            <h3 className="-my-3 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                <span className="font-medium text-gray-900">Disponibilité</span>
                <span className="ml-6 flex items-center">
                  <ChevronDownIcon
                    className={`h-5 w-5 transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </Disclosure.Button>
            </h3>
            <Disclosure.Panel className="pt-6">
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer hover:text-primary-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                    checked={currentFilters.stock_status === 'instock'}
                    onChange={(e) =>
                      handleFilterChange({
                        stock_status: e.target.checked ? 'instock' : undefined,
                      })
                    }
                  />
                  <span className="ml-3 text-sm">En stock uniquement</span>
                </label>
                <label className="flex items-center cursor-pointer hover:text-primary-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                    checked={currentFilters.on_sale === true}
                    onChange={(e) =>
                      handleFilterChange({
                        on_sale: e.target.checked ? true : undefined,
                      })
                    }
                  />
                  <span className="ml-3 text-sm">En promotion</span>
                </label>
                <label className="flex items-center cursor-pointer hover:text-primary-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                    checked={currentFilters.featured === true}
                    onChange={(e) =>
                      handleFilterChange({
                        featured: e.target.checked ? true : undefined,
                      })
                    }
                  />
                  <span className="ml-3 text-sm">Produits vedettes</span>
                </label>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );

  return (
    <>
      {/* Filtres mobiles */}
      <Transition.Root show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-lg font-medium text-gray-900">Filtres</h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Fermer le menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4 border-t border-gray-200">
                  <div className="px-4 py-6">
                    <FiltersContent />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Bouton filtres mobile */}
      <div className="flex lg:hidden">
        <button
          type="button"
          className="btn-secondary flex items-center gap-2"
          onClick={() => setMobileFiltersOpen(true)}
        >
          <FunnelIcon className="h-5 w-5" />
          Filtres
        </button>
      </div>

      {/* Filtres desktop */}
      <div className="hidden lg:block">
        <FiltersContent />
      </div>
    </>
  );
};

